// react
import { useRef } from 'react'

// external libs
import { Box, Button, makeStyles, Paper, Typography } from '@material-ui/core'
import { useField } from 'react-final-form'

// components
import Textfield from '../../../../../control/textfield/textfield'

// hooks
import useTempFile from '../../../../../hooks/useTempFile'
import useTranslate from '../../../../../i18n/useTranslate'

// services & types
import { DocumentInfo } from '../../../../../services/web/useOfficeFile'

// constants & utils
import { convertDateToFormatEN, convertDateToFormatTH } from '../../../../../utilities/date'
import { getFileIconPath } from '../../../../../utilities/pathImg'

export interface DocumentInfoFieldProps {
    no: number
    name: string
    documentInfo: DocumentInfo
    onDelete?: (documentInfo: DocumentInfo) => void
    onDeleteTempFile?: (fileToken: string) => void
}

function DocumentInfoField(props: DocumentInfoFieldProps) {
    const { no, name, documentInfo, onDelete, onDeleteTempFile } = props

    const classes = useStyles()
    const translate = useTranslate()
    const tempFile = useTempFile()

    const { input } = useField<DocumentInfo[]>(name)
    const inputRef = useRef<HTMLInputElement | null>(null)

    async function handleClearFile(fileToken: string) {
        input.onChange({
            ...documentInfo,
            fileInfo: [],
        })
        onDeleteTempFile && onDeleteTempFile(fileToken)
    }

    async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
        const files = e.target.files

        if (!files || files.length === 0) {
            return false
        }

        const file = files[0]

        const isValid = tempFile.validate(file)

        if (!isValid) {
            return false
        }

        const fileUploaded = await tempFile.upload(file)

        if (!fileUploaded) {
            return
        }

        const fileInfo = { ...fileUploaded, date: new Date().toISOString() }

        input.onChange({
            ...documentInfo,
            fileInfo: [fileInfo],
        })
    }

    function handleUploadClick() {
        inputRef.current?.click()
    }

    const getDisplayDate = (date?: string) => {
        let display = ''
        if (date) {
            display =
                translate.languageStore.currentLanguage === translate.languageKey.th
                    ? 'ล่าสุดวันที่ ' + convertDateToFormatTH(date)
                    : 'Last updated ' + convertDateToFormatEN(date)
        }
        return display
    }

    const fileInfo = documentInfo.fileInfo?.[0]

    return (
        <Box display="flex" flexDirection="column" width="100%" className={classes.documentInfoField}>
            <Box display="flex" justifyContent="space-between" mb={3}>
                <Typography variant="h5" style={{ fontWeight: 700 }}>
                    เอกสารรายการที่ {no}
                </Typography>
                <Button
                    size="medium"
                    variant="contained"
                    color="secondary"
                    onClick={() => onDelete && onDelete(documentInfo)}
                >
                    ลบ
                </Button>
            </Box>

            <Textfield
                name={`${name}.title`}
                maxLength={250}
                materialInputProps={{
                    label: 'ชื่อเอกสาร',
                    placeholder: 'กรอกชื่อเอกสาร',
                    size: 'small',
                }}
            />
            <Textfield
                name={`${name}.remark`}
                materialInputProps={{
                    label: 'หมายเหตุ',
                    placeholder: 'กรอกหมายเหตุ',
                    size: 'small',
                    rows: 2,
                    multiline: true,
                }}
                maxLength={1000}
            />

            <input
                ref={inputRef}
                type="file"
                accept=".png,.jpeg,.tiff,.pdf,.doc,.docx"
                hidden
                onChange={handleFileChange}
            />

            <Box display="flex" justifyContent="space-between">
                <Typography variant={'subtitle2'}>อัปโหลดเอกสาร</Typography>
                <Button size="small" variant="contained" color="primary" onClick={handleUploadClick}>
                    {translate.get('BUTTON.UPLOAD')}
                </Button>
            </Box>

            <Paper variant="outlined" className={classes.filePreview}>
                {fileInfo ? (
                    <Box display="flex" style={{ gap: 8 }}>
                        <img src={process.env.PUBLIC_URL + getFileIconPath(fileInfo.filename)} alt="file icon" />
                        <a href={fileInfo.path} target="_blank" rel="noreferrer">
                            <Typography variant={'body2'} style={{ wordBreak: 'break-word' }}>
                                {fileInfo.filename} {getDisplayDate(fileInfo.date)}
                            </Typography>
                        </a>
                        <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            onClick={() => handleClearFile(fileInfo.fileToken)}
                        >
                            {translate.get('BUTTON.DELETE')}
                        </Button>
                    </Box>
                ) : (
                    <Typography variant={'body2'} style={{ wordBreak: 'break-word' }}>
                        {translate.get('LABEL.NO_FILE_UPLOAD')}
                    </Typography>
                )}
            </Paper>
        </Box>
    )
}

const useStyles = makeStyles(() => ({
    documentInfoField: {
        paddingBottom: 32,
        borderBottom: 'dashed 1px rgba(0, 0, 0, 0.12)',

        '&:last-child': {
            borderBottom: 'none',
            paddingBottom: 0,
        },
    },

    filePreview: {
        marginTop: 8,
        padding: 16,

        '& img': {
            width: 24,
            height: 24,
            flexShrink: 0,
        },
        '& a': {
            flexGrow: 1,
        },
        '& button': {
            flexShrink: 0,
        },
    },
}))
export default DocumentInfoField
